/**
 * Centralised fetch wrapper. Every API call goes through `request()` so:
 *
 *   - Auth header is attached automatically when a session exists.
 *   - Error envelopes from the backend (`{error, code, hint}`) are surfaced
 *     as typed exceptions, not silently swallowed.
 *   - 401 responses trigger a session wipe + redirect to login (next paint).
 *
 * The base URL is always same-origin (`/api/v1/...`). In dev, Vite proxies it
 * to the metsadata Go backend on :8081 (see vite.config.ts).
 */
import { bearerHeader, clearSession } from '../state/session.ts';
import type { APIError } from '../types/api.ts';

export const API_BASE = '/api/v1';

export class APIRequestError extends Error {
  status: number;
  body: APIError;
  constructor(status: number, body: APIError) {
    super(body.error || `HTTP ${status}`);
    this.status = status;
    this.body = body;
    this.name = 'APIRequestError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  /** Extra query parameters appended to the URL (numbers/strings only). */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Set true for public endpoints (login, register) that must not 401-redirect. */
  skipAuthRedirect?: boolean;
  signal?: AbortSignal;
}

/**
 * Listener invoked when the API responds with 401. The bootstrap (main.ts)
 * registers a handler that re-renders the login view. Keeping this decoupled
 * from the fetch logic means tests don't have to mock window.location.
 */
let unauthorizedHandler: (() => void) | null = null;
export function onUnauthorized(handler: () => void): void {
  unauthorizedHandler = handler;
}

function buildURL(path: string, query?: RequestOptions['query']): string {
  let url = path.startsWith('/api/') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  return url;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method || 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...bearerHeader(),
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(buildURL(path, opts.query), {
    method,
    headers,
    body,
    signal: opts.signal,
  });

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Non-JSON body (e.g. HTML error page from a proxy). Surface as-is.
      payload = { error: text };
    }
  }

  if (!res.ok) {
    const err = (payload as { error?: string } | null) ?? {};
    if (res.status === 401 && !opts.skipAuthRedirect) {
      clearSession();
      if (unauthorizedHandler) unauthorizedHandler();
    }
    throw new APIRequestError(res.status, err as APIError);
  }

  // Backend convention: most endpoints wrap their payload in `{data: ...}`.
  // We return the wrapper as-is and let the typed call sites destructure;
  // this matches the shape declared in src/types/api.ts.
  return payload as T;
}

/** Shortcut for endpoints whose success envelope is `{data: T}`. */
export async function requestData<T>(path: string, opts?: RequestOptions): Promise<T> {
  const wrapped = await request<{ data: T }>(path, opts);
  return wrapped.data;
}
